/**
 * The event player: one game-core event at a time, as an animation. The
 * switch is exhaustive over `Event['type']` (lint enforces it) and every case
 * returns the type it handled, so a type that reached no case throws instead
 * of being counted. Every event has its gesture (slice 1: movement, attacks,
 * hits, falls and the floor's markers; slice 2b: casts, tosses, charges,
 * skips, rings, the boss's fall, the egg, escapes), lifted from the
 * dungeon-floors prototype's `dungeon-events.js`: a unit is moved, flashed or
 * faded, never scaled. Text belongs to the DOM (ADR 0005): the shell turns the
 * same events into labels, badges and banners at the moment `play` reaches
 * each one (`onEvent`). A unit the player cannot see snaps: no time is spent
 * on it. Nothing here decides a rule: positions, facings and who is down come
 * from the events and the state after the command.
 *
 * Sprite units (slice 3) also change anim here, alongside the gesture (a
 * sprite is moved, flashed or faded, never scaled or rotated; `sprite-anim.ts`
 * holds the clock): `moved` walks while its tween runs; `attacked` plays
 * `attack` and resolves at its `hitFrame`, so the `damaged` or `missed` after
 * it lands on the strike while the recovery frames finish on their own;
 * `skill_used` and `charge_fired` play `cast` the same way; `damaged` plays
 * `hurt`; `unit_defeated` and `boss_defeated` play `faint` to its held last
 * frame (no fall rotation: the contract draws the collapse in the cell);
 * `revived` stands back to `idle`; `charge_started` loops `charge` until the
 * unit's own `charge_fired` or `charge_interrupted`; sleep (applied, or a turn
 * skipped for it) loops `sleep` until `woke_up` or the status ends; `escaped`
 * fades the plane. Every other event changes no anim.
 */
import type { Dir, Event, Pos, RunState, Unit } from '@ab/game-core';
import { dirOffset } from '@ab/game-core';
import type * as THREE from 'three';
import { presentation } from './constants.ts';
import { itemOwner, springOwner } from './markers.ts';
import type { Palette } from './palette.ts';
import type { UnitLayer, UnitPose } from './units.ts';

/** What the event player needs from the view. */
export type Stage = {
  readonly units: UnitLayer;
  readonly palette: Palette;
  run(durationMs: number, step: (t: number) => void): Promise<void>;
  /** Builds the floor of `after` (a `floor_entered`), without units: `unit_spawned` adds them. */
  enterFloor(after: RunState): void;
  /** A unit has reached a tile (fog follows the leader). */
  arrived(unitId: string): void;
  /** Whether the player can see the unit now (party units always; enemies on explored tiles). */
  visible(pose: UnitPose): boolean;
  setMarkerHidden(owner: string, hidden: boolean): void;
  setMarkerOffset(owner: string, dy: number): void;
  /** A ring flash on the ground under game coordinates, over `ms`. */
  ring(x: number, y: number, color: THREE.Color, burst: boolean, ms: number): Promise<void>;
  /** Shows the egg billboard at a point, or hides it (null). */
  setEgg(at: { readonly x: number; readonly y: number; readonly lift: number } | null): void;
  /** Where the last boss fell (the egg starts there). */
  lastBoss: Pos | null;
};

/** Where one event sits in its command's batch. */
export type EventContext = {
  readonly after: RunState;
  /**
   * For a `moved`: whether the unit's facing after the command is its facing
   * after this move (its last `moved` of the batch, with no `turned` of its
   * own after it). Otherwise the move faces its own direction.
   */
  readonly facingFromAfter: boolean;
  /** The event after this one in the command's batch, if any. */
  readonly next: Event | undefined;
};

const a = presentation.anim;

/** Directions counter-clockwise from east, 45 degrees apart. */
const compass: readonly Dir[] = ['e', 'ne', 'n', 'nw', 'w', 'sw', 's', 'se'];

/** The nearest of the eight directions to a vector (a one-tile step is exact; a leap rounds). */
export const dirToward = (dx: number, dy: number): Dir | null => {
  if (dx === 0 && dy === 0) return null;
  const octant = Math.round(Math.atan2(dy, dx) / (Math.PI / 4));
  return compass[((octant % 8) + 8) % 8] ?? null;
};

const unitIn = (state: RunState, id: string): Unit | undefined => state.units.find((u) => u.id === id);

/**
 * A sprite one-shot that resolves at its hit frame (attack, cast), together
 * with the gesture: the event ends when both have. The one-shot and the wait
 * start at the same moment, so the strike shows its `hitFrame` exactly when
 * the wait of the `ms` before it ends. A proxy, a unit the player cannot see
 * or a sheet without the anim only runs the gesture.
 */
const strike = async (stage: Stage, pose: UnitPose, anim: string, gesture: Promise<void>): Promise<void> => {
  const toHit = stage.units.oneShot(pose, anim);
  await Promise.all([gesture, toHit > 0 && stage.visible(pose) ? stage.run(toHit, () => undefined) : undefined]);
};

/** How long a fall takes: the proxy's fall, or a sprite's whole `faint` if that is longer. */
const fallMs = (stage: Stage, pose: UnitPose, proxyMs: number): number => Math.max(proxyMs, stage.units.animMs(pose, 'faint'));

const lunge = async (stage: Stage, pose: UnitPose, toward: Pos | null): Promise<void> => {
  if (!stage.visible(pose)) return;
  const d = toward === null ? dirOffset[pose.facing] : { x: toward.x - pose.x, y: toward.y - pose.y };
  const length = Math.hypot(d.x, d.y) || 1;
  const nx = (d.x / length) * presentation.lungeDistance;
  const ny = (d.y / length) * presentation.lungeDistance;
  await stage.run(a.lungeMs, (t) => {
    const k = Math.sin(Math.PI * t);
    pose.lungeX = nx * k;
    pose.lungeY = ny * k;
  });
  pose.lungeX = 0;
  pose.lungeY = 0;
};

const flash = (stage: Stage, pose: UnitPose, color: Palette[keyof Palette]): Promise<void> => pulse(stage, pose, color, a.flashMs);

const g = presentation.gesture;

/** A hop: `lift` rises and falls once over `ms`. */
const hop = async (stage: Stage, pose: UnitPose, height: number, ms: number): Promise<void> => {
  if (!stage.visible(pose)) return;
  await stage.run(ms, (t) => {
    pose.lift = Math.sin(Math.PI * t) * height;
  });
  pose.lift = 0;
};

/** A cast: a hop with a glow toward the theme's cast colour. */
const cast = async (stage: Stage, pose: UnitPose): Promise<void> => {
  if (!stage.visible(pose)) return;
  pose.flash = stage.palette.cast;
  await stage.run(a.castMs, (t) => {
    const k = Math.sin(Math.PI * t);
    pose.lift = k * g.castLift;
    pose.flashAmount = k;
  });
  pose.lift = 0;
  pose.flash = null;
  pose.flashAmount = 0;
};

/** A flash toward `color` over `ms` (the hit and heal flashes use `flashMs`). */
const pulse = async (stage: Stage, pose: UnitPose, color: THREE.Color, ms: number): Promise<void> => {
  if (!stage.visible(pose)) return;
  pose.flash = color;
  await stage.run(ms, (t) => {
    pose.flashAmount = Math.sin(Math.PI * t);
  });
  pose.flash = null;
  pose.flashAmount = 0;
};

/** An offset along the facing (negative: backward), out and back over `ms`. */
const nudge = async (stage: Stage, pose: UnitPose, distance: number, ms: number): Promise<void> => {
  if (!stage.visible(pose)) return;
  const d = dirOffset[pose.facing];
  const length = Math.hypot(d.x, d.y) || 1;
  await stage.run(ms, (t) => {
    const k = Math.sin(Math.PI * t) * distance;
    pose.swayX = (d.x / length) * k;
    pose.swayY = (d.y / length) * k;
  });
  pose.swayX = 0;
  pose.swayY = 0;
};

const unhandled = (e: never): never => {
  throw new Error(`the renderer has no case for event ${JSON.stringify(e)}`);
};

/** Plays one event; returns the type it handled (the view counts it). */
export const playEvent = async (stage: Stage, e: Event, ctx: EventContext): Promise<Event['type']> => {
  const { units } = stage;
  const { after } = ctx;
  switch (e.type) {
    case 'floor_entered':
      stage.enterFloor(after);
      return e.type;
    case 'unit_spawned': {
      const unit = unitIn(after, e.unit);
      if (unit !== undefined) units.add({ ...unit, pos: e.pos, down: false });
      stage.arrived(e.unit);
      return e.type;
    }
    case 'turned': {
      const pose = units.get(e.unit);
      if (pose) pose.facing = e.facing;
      return e.type;
    }
    case 'moved': {
      const pose = units.get(e.unit);
      if (!pose) return e.type;
      const dx = e.to.x - e.from.x;
      const dy = e.to.y - e.from.y;
      // A successful move emits no `turned`. Its last move of the command faces as the unit does after the
      // command; an earlier one (a swap before the leader's own step, a leap before a turn) faces its own way.
      const afterFacing = ctx.facingFromAfter ? unitIn(after, e.unit)?.facing : undefined;
      pose.facing = afterFacing ?? dirToward(dx, dy) ?? pose.facing;
      const seenFrom = stage.visible(pose);
      pose.x = e.to.x;
      pose.y = e.to.y;
      const seenTo = stage.visible(pose);
      // A unit the player cannot see at either end snaps: no animation time is spent on it.
      if (seenFrom || seenTo) {
        // One segment, however long: a leap of more than one tile is one straight glide; a sprite walks through it.
        pose.walking = true;
        await stage.run(Math.hypot(dx, dy) * a.moveMsPerTile, (t) => {
          pose.x = e.from.x + dx * t;
          pose.y = e.from.y + dy * t;
        });
        pose.walking = false;
      }
      stage.arrived(e.unit);
      return e.type;
    }
    case 'attacked': {
      const pose = units.get(e.unit);
      const target = e.target === null ? undefined : units.get(e.target);
      if (pose) await strike(stage, pose, 'attack', lunge(stage, pose, target ? { x: target.x, y: target.y } : null));
      return e.type;
    }
    case 'missed': {
      const pose = units.get(e.unit);
      const target = units.get(e.target);
      if (pose) await lunge(stage, pose, target ? { x: target.x, y: target.y } : null);
      return e.type;
    }
    case 'damaged': {
      // A burn or poison tick (source null) flashes too.
      const pose = units.get(e.unit);
      if (!pose) return e.type;
      units.oneShot(pose, 'hurt');
      await flash(stage, pose, stage.palette.hit);
      return e.type;
    }
    case 'healed': {
      const pose = units.get(e.unit);
      if (pose) await flash(stage, pose, stage.palette.heal);
      return e.type;
    }
    case 'unit_defeated': {
      const pose = units.get(e.unit);
      if (!pose) return e.type;
      pose.charging = false;
      pose.sleeping = false;
      // The boss falls once, longer, on its own boss_defeated right after this.
      if (pose.boss && ctx.next?.type === 'boss_defeated' && ctx.next.unit === e.unit) return e.type;
      pose.down = true;
      pose.anim.oneShot = null;
      if (stage.visible(pose)) {
        await stage.run(fallMs(stage, pose, a.fallMs), (t) => {
          pose.fall = Math.max(pose.fall, t);
        });
      }
      pose.fall = 1;
      // An enemy leaves the floor after it falls; a party unit stays down where it fell.
      if (pose.side === 'enemy') units.remove(e.unit);
      return e.type;
    }
    case 'boss_defeated': {
      // A longer fall with a flash; the egg starts where it fell.
      const pose = units.get(e.unit);
      if (!pose) return e.type;
      stage.lastBoss = { x: pose.x, y: pose.y };
      pose.down = true;
      pose.anim.oneShot = null;
      if (stage.visible(pose)) {
        pose.flash = stage.palette.hit;
        await stage.run(fallMs(stage, pose, a.bossFallMs), (t) => {
          pose.fall = Math.max(pose.fall, t);
          pose.flashAmount = Math.sin(Math.PI * Math.min(1, t * 2));
        });
        pose.flash = null;
        pose.flashAmount = 0;
      }
      pose.fall = 1;
      units.remove(e.unit);
      return e.type;
    }
    case 'revived': {
      // game-core revives only at the fall (a charm's `revive`), keeping the tile: the unit stands up where it fell, in a rising glow.
      const pose = units.get(e.unit);
      if (!pose) {
        const unit = unitIn(after, e.unit);
        if (unit !== undefined) units.add({ ...unit, down: false });
        return e.type;
      }
      pose.down = false;
      if (stage.visible(pose)) {
        pose.flash = stage.palette.heal;
        await stage.run(a.standMs, (t) => {
          pose.fall = 1 - t;
          pose.lift = Math.sin(Math.PI * t) * g.reviveLift;
          pose.flashAmount = Math.sin(Math.PI * t);
        });
        pose.flash = null;
        pose.flashAmount = 0;
        pose.lift = 0;
      }
      pose.fall = 0;
      return e.type;
    }
    case 'item_picked_up':
      stage.setMarkerHidden(itemOwner(e.pos), true);
      return e.type;
    case 'item_left': {
      // What did not fit stays on the floor: the marker stays (or comes back) and bobs.
      const owner = itemOwner(e.pos);
      stage.setMarkerHidden(owner, false);
      await stage.run(a.bobMs, (t) => {
        stage.setMarkerOffset(owner, Math.sin(Math.PI * t) * presentation.bobHeight);
      });
      stage.setMarkerOffset(owner, 0);
      return e.type;
    }
    case 'spring_used':
      stage.setMarkerHidden(springOwner(e.pos), true);
      return e.type;
    case 'skill_used': {
      const pose = units.get(e.unit);
      if (pose) await strike(stage, pose, 'cast', cast(stage, pose));
      return e.type;
    }
    case 'item_used': {
      // A small toss, then a flash on each target at once.
      const pose = units.get(e.unit);
      if (pose) await hop(stage, pose, g.tossLift, a.tossMs);
      await Promise.all(
        e.targets.flatMap((id) => {
          const target = units.get(id);
          return target ? [pulse(stage, target, stage.palette.cast, a.targetFlashMs)] : [];
        }),
      );
      return e.type;
    }
    case 'status_applied':
    case 'status_cured':
    case 'status_expired': {
      // The badge row over the unit is DOM (the shell); the unit pulses in the status colour. A sprite sleeps while asleep.
      const pose = units.get(e.unit);
      if (!pose) return e.type;
      if (e.status === 'sleep') pose.sleeping = e.type === 'status_applied' && !pose.down;
      await pulse(stage, pose, stage.palette.status, a.statusMs);
      return e.type;
    }
    case 'stage_changed': {
      // A chevron pulse: up for a raised stage, down for a lowered one (the chevron itself is a DOM badge).
      // The event carries the new total, so the direction is its change from the total last shown.
      const pose = units.get(e.unit);
      if (!pose) return e.type;
      const sign = Math.sign(e.stages - (pose.stageTotals.get(e.stat) ?? 0));
      pose.stageTotals.set(e.stat, e.stages);
      if (sign !== 0 && stage.visible(pose)) {
        await stage.run(a.stageMs, (t) => {
          pose.lift = Math.sin(Math.PI * t) * g.stageLift * sign;
        });
        pose.lift = 0;
      }
      return e.type;
    }
    case 'charge_started': {
      // A held, trembling pose that lasts until the unit's own charge_fired or charge_interrupted.
      const pose = units.get(e.unit);
      if (pose) pose.charging = true;
      return e.type;
    }
    case 'charge_fired': {
      const pose = units.get(e.unit);
      if (!pose) return e.type;
      pose.charging = false;
      await strike(stage, pose, 'cast', cast(stage, pose));
      return e.type;
    }
    case 'charge_interrupted': {
      // The tremble stops with a small recoil.
      const pose = units.get(e.unit);
      if (!pose) return e.type;
      pose.charging = false;
      await nudge(stage, pose, -g.recoil, a.recoilMs);
      return e.type;
    }
    case 'passive_triggered': {
      const pose = units.get(e.unit);
      if (pose && stage.visible(pose)) await stage.ring(pose.x, pose.y, stage.palette.status, false, a.ringMs);
      return e.type;
    }
    case 'action_skipped': {
      // A sway for sleep, stun, paralysis or confusion; a sprite skipping for sleep sleeps.
      const pose = units.get(e.unit);
      if (pose && e.reason === 'sleep') pose.sleeping = !pose.down;
      if (pose && stage.visible(pose)) {
        await stage.run(a.skipMs, (t) => {
          pose.swayX = Math.sin(t * g.swayWaves) * g.sway * (1 - t);
        });
        pose.swayX = 0;
      }
      return e.type;
    }
    case 'woke_up': {
      const pose = units.get(e.unit);
      if (!pose) return e.type;
      pose.sleeping = false;
      await hop(stage, pose, g.popLift, a.popMs);
      return e.type;
    }
    case 'item_auto_used': {
      // The charm shatters: a burst at the fallen unit (its `revived` follows).
      const pose = units.get(e.unit);
      if (pose && stage.visible(pose)) {
        await Promise.all([stage.ring(pose.x, pose.y, stage.palette.cast, true, a.burstMs), pulse(stage, pose, stage.palette.cast, a.burstMs)]);
      }
      return e.type;
    }
    case 'egg_obtained': {
      // The egg appears where the boss fell and hops to the leader.
      const from = stage.lastBoss;
      const leaderId = after.party.find((id) => after.units.some((u) => u.id === id && !u.down)) ?? after.party[0];
      const leader = leaderId === undefined ? undefined : units.get(leaderId);
      if (from !== null && leader !== undefined) {
        const to = { x: leader.x, y: leader.y };
        await stage.run(a.eggHopMs, (t) => {
          stage.setEgg({ x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t, lift: Math.sin(Math.PI * t) * g.eggArc });
        });
      }
      stage.setEgg(null);
      return e.type;
    }
    case 'escaped': {
      // The leader fades out at its tile, rising a little; then it is gone (run_escaped follows).
      const pose = units.get(e.unit);
      if (!pose) return e.type;
      if (stage.visible(pose)) {
        await stage.run(a.escapeMs, (t) => {
          pose.fade = t;
          pose.lift = t * g.escapeRise;
        });
      }
      pose.gone = true;
      return e.type;
    }
    case 'exp_gained':
    case 'level_up':
    case 'skill_learned':
    case 'items_lost':
    case 'round_ended':
    case 'floor_cleared':
    case 'run_completed':
    case 'run_failed':
    case 'run_escaped':
      // Labels and banners only: the shell draws them in the DOM (ADR 0005); the next floor arrives with its own floor_entered.
      return e.type;
    case 'ap_changed':
    case 'sp_changed':
      // The text log and the HUD carry these.
      return e.type;
  }
  return unhandled(e);
};
